import React from 'react';
import PropTypes from 'prop-types';

import AuthLayout from '@dashboard/layouts/AuthLayout';

import { FormElement, Label } from '@dashboard/components/Form';
import { Button } from '@dashboard/components/ui/button';
import { cn } from '@dashboard/lib/utils';
import { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import Meta from '@dashboard/components/Meta';
import {
  AuthFooter,
  AuthHeader,
  AuthLink,
  PasswordInput,
  authButtonClassName,
  PASSWORD_TOO_SHORT,
  passwordHint,
} from '@dashboard/components/AuthForm';

const propTypes = {
  form: PropTypes.object.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
};

const AccountResetPassword = props => {
  const { form, handleSubmit, handleInputChange, submitting } = props;
  const isSaved = form.saved && !form.message;

  return (
    <>
      <Meta
        meta={{
          title: 'Reset your password',
        }}
        noIndex
      />

      <AuthLayout>
        {isSaved ? (
          <div role="status">
            <AuthHeader
              title="Password updated"
              description="You can now log in with your new password."
            />
            <Button to="/login" size="xl" className={cn('w-full', authButtonClassName)}>
              Log in
            </Button>
          </div>
        ) : form.linkExpired ? (
          <>
            <div role="alert">
              <AuthHeader
                title="This reset link has expired"
                description="Reset links work once and only for a short time. Request a new one and we will email it to you."
              />
              <Button
                to="/account/forgot-password"
                size="xl"
                className={cn('w-full', authButtonClassName)}
              >
                Request a new link
              </Button>
            </div>

            <AuthFooter>
              Back to <AuthLink path="/login">log in</AuthLink>
            </AuthFooter>
          </>
        ) : (
          <>
            <AuthHeader title="Set a new password" />

            <form onSubmit={e => handleSubmit(e)}>
              <FormElement>
                <Label htmlFor="password">New password</Label>
                <PasswordInput
                  id="password"
                  name="password"
                  value={form.state.password}
                  onChange={e => handleInputChange(e)}
                  autoComplete="new-password"
                  aria-invalid={form.message === PASSWORD_TOO_SHORT ? true : undefined}
                  required
                />
                {form.message !== PASSWORD_TOO_SHORT && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-muted-foreground">
                    {passwordHint}
                  </p>
                )}
              </FormElement>

              {form.message && <FormErrorBase className="mb-4">{form.message}</FormErrorBase>}

              <Button
                type="submit"
                size="xl"
                disabled={submitting}
                className={cn('w-full', authButtonClassName)}
              >
                Save password
              </Button>
            </form>

            <AuthFooter>
              Back to <AuthLink path="/login">log in</AuthLink>
            </AuthFooter>
          </>
        )}
      </AuthLayout>
    </>
  );
};

AccountResetPassword.propTypes = propTypes;

export default AccountResetPassword;
